/**
 * TimelinePanel — the INTERNAL SAGE/client surface (events, comms, records).
 *
 * Distinct from FeedPanel (external world). Live via sage-intel-mcp:
 *   - query_timeline       → recent cross-source SAGE events
 *   - search_communications → free-text search of texts/calls/emails (search mode)
 * Output shapes vary by worker version, so field access is defensive.
 */
import { useMemo, useState } from "react";
import { Search, X, Clock } from "lucide-react";
import McpCard from "../components/McpCard";
import { useMcpTool } from "../lib/useMcpTool";
import { PanelScroll } from "./ClientsPanel";
import { setChatContext } from "../lib/chatContext";

interface TLEvent {
  id?: string | number;
  ts?: string; timestamp?: string; date?: string; created_at?: string; occurred_at?: string;
  title?: string; summary?: string; text?: string; body?: string; description?: string; preview?: string;
  type?: string; kind?: string; channel?: string; source?: string; category?: string;
  contact?: string; entity?: string; client?: string; folder?: string; with?: string;
  [k: string]: unknown;
}

function rowsFrom(data: unknown): TLEvent[] {
  if (Array.isArray(data)) return data as TLEvent[];
  if (data && typeof data === "object") {
    const o = data as Record<string, unknown>;
    for (const k of ["events", "rows", "results", "items", "timeline", "communications"]) {
      if (Array.isArray(o[k])) return o[k] as TLEvent[];
    }
  }
  return [];
}

const tsOf = (e: TLEvent) => e.ts ?? e.timestamp ?? e.occurred_at ?? e.created_at ?? e.date ?? "";
const titleOf = (e: TLEvent) => e.title ?? e.summary ?? e.text ?? e.body ?? e.preview ?? e.description ?? "(event)";
const tagOf = (e: TLEvent) => e.type ?? e.kind ?? e.channel ?? e.source ?? e.category ?? "";
const whoOf = (e: TLEvent) => e.contact ?? e.entity ?? e.client ?? e.with ?? e.folder ?? "";

function fmtTs(s: string): string {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function TimelinePanel() {
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");

  const timeline = useMcpTool<unknown>("intel", "query_timeline", { limit: 60 }, { enabled: !submitted, timeoutMs: 25_000 });
  const search = useMcpTool<unknown>("intel", "search_communications", { query: submitted, limit: 60 }, { enabled: !!submitted, timeoutMs: 25_000 });

  const active = submitted ? search : timeline;
  const events = useMemo(() => {
    const r = rowsFrom(active.data);
    return [...r].sort((a, b) => (tsOf(b) > tsOf(a) ? 1 : tsOf(b) < tsOf(a) ? -1 : 0));
  }, [active.data]);

  return (
    <PanelScroll accentVar="--biome-tech" title="Timeline" subtitle="internal · SAGE events + comms · sage-intel-mcp">
      <form
        onSubmit={(e) => { e.preventDefault(); setSubmitted(query.trim()); }}
        className="flex items-center gap-2 rounded-xl border bg-surface px-3 h-10"
      >
        <Search size={15} className="text-text-dim shrink-0" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search communications (texts, calls, emails)…"
          className="flex-1 bg-transparent text-text placeholder-text-dim outline-none text-body-sm min-w-0"
        />
        {submitted && (
          <button type="button" onClick={() => { setQuery(""); setSubmitted(""); }} className="text-text-dim hover:text-text shrink-0" title="Clear search"><X size={15} /></button>
        )}
      </form>

      <McpCard
        title={submitted ? `Search · "${submitted}"${events.length ? ` (${events.length})` : ""}` : `Recent SAGE timeline${events.length ? ` (${events.length})` : ""}`}
        loading={active.loading}
        error={active.error}
        onRefresh={active.refresh}
        accentVar="--biome-tech"
      >
        {events.length === 0 ? (
          <div className="text-body-sm text-text-dim flex items-center gap-2"><Clock size={14} /> {submitted ? "No matching communications." : "No timeline events returned."}</div>
        ) : (
          <div className="space-y-0">
            {events.map((e, i) => {
              const who = whoOf(e);
              const tag = tagOf(e);
              return (
                <button key={e.id ?? i} onClick={() => setChatContext({ label: `Event: ${titleOf(e).slice(0, 44)}`, detail: `${fmtTs(tsOf(e))}${tag ? ` · ${tag}` : ""}${who ? ` · ${who}` : ""}\n${titleOf(e)}` })}
                  className="w-full text-left relative pl-5 pb-3 border-l-2 border-border-strong last:border-l-transparent group">
                  <span className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-primary ring-2 ring-bg" />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-caption text-text-dim">{fmtTs(tsOf(e))}</span>
                    {tag && <span className="px-1.5 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-medium bg-primary-muted text-primary">{tag}</span>}
                    {who && <span className="text-caption text-text-muted font-medium">{who}</span>}
                  </div>
                  <div className="text-body-sm text-text mt-0.5 leading-snug group-hover:text-primary transition-colors">{titleOf(e)}</div>
                </button>
              );
            })}
          </div>
        )}
      </McpCard>
    </PanelScroll>
  );
}
